import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { FadeUp, MaskText } from "@/components/Reveal";
import { CAPABILITIES } from "@/data/content";
import {
    Film,
    MonitorPlay,
    Wand2,
    SlidersHorizontal,
    Trophy,
    Globe2,
    Smartphone,
} from "lucide-react";

const ICONS = {
    "feature-films": Film,
    "web-series": MonitorPlay,
    "end-to-end-production": Wand2,
    "post-production": SlidersHorizontal,
    "festival-market-delivery": Trophy,
    "global-distribution": Globe2,
    "micro-series": Smartphone,
};

export const Capabilities = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const sectionProgress = useSpring(scrollYProgress, {
        stiffness: 90,
        damping: 26,
        mass: 0.4,
    });
    const orbY = useTransform(sectionProgress, [0, 1], [-80, 80]);
    const orbX = useTransform(sectionProgress, [0, 1], [20, -40]);
    const orbY2 = useTransform(sectionProgress, [0, 1], [60, -60]);

    return (
        <section
            id="capabilities"
            ref={ref}
            data-testid="capabilities-section"
            className="relative py-28 sm:py-36 lg:py-40 bg-[hsl(var(--bg-elevated))] overflow-hidden"
        >
            <motion.div
                aria-hidden
                style={{ y: orbY, x: orbX }}
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(ellipse, hsla(38,92%,62%,0.16), transparent 60%)",
                        filter: "blur(50px)",
                    }}
                />
            </motion.div>
            <motion.div
                aria-hidden
                style={{ y: orbY2 }}
                className="absolute bottom-0 -right-20 w-[600px] h-[600px] pointer-events-none"
            >
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, hsla(176,62%,44%,0.10), transparent 60%)",
                        filter: "blur(50px)",
                    }}
                />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="flex items-baseline justify-between gap-6 mb-14">
                    <FadeUp>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]">
                            <span className="text-[hsl(var(--accent-gold))]">❖</span> 04 /
                            Capabilities
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.06}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                            From Script to Screen
                        </div>
                    </FadeUp>
                </div>

                <MaskText
                    as="h2"
                    lines={["A House Built To", "Carry the Whole Pipeline."]}
                    className="font-display text-[clamp(2rem,6vw,5rem)] leading-[0.95] mb-16 max-w-5xl"
                />

                <div className="grid grid-cols-12 gap-6">
                    {CAPABILITIES.map((c, i) => {
                        const Icon = ICONS[c.slug] || Film;
                        const isFeatured = i === 2; // End-to-End larger
                        return (
                            <FadeUp
                                key={c.slug}
                                delay={i * 0.05}
                                className={`col-span-12 sm:col-span-6 ${
                                    isFeatured ? "lg:col-span-6 lg:row-span-2" : "lg:col-span-4"
                                }`}
                            >
                                <article
                                    data-testid={`capability-item-${c.slug}`}
                                    data-cursor="hover"
                                    className={`relative cine-card rounded-2xl p-7 sm:p-8 h-full flex flex-col justify-between min-h-[210px] ${
                                        isFeatured ? "sm:min-h-[440px]" : ""
                                    }`}
                                >
                                    {c.comingSoon && (
                                        <span
                                            data-testid={`capability-coming-soon-${c.slug}`}
                                            className="absolute top-5 right-5 inline-flex items-center gap-2 rounded-full bg-black/60 border border-white/15 px-3 py-1 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]"
                                        >
                                            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent-gold))] animate-pulse" />
                                            Coming Soon
                                        </span>
                                    )}
                                    <div className="flex items-start justify-between">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                            {c.label}
                                        </span>
                                        {!c.comingSoon && (
                                            <Icon
                                                className="h-4 w-4 text-[hsl(var(--accent-gold))]"
                                                strokeWidth={1.5}
                                            />
                                        )}
                                    </div>
                                    <div className="mt-12">
                                        <h3
                                            className={`font-display tracking-tight leading-[0.95] ${
                                                isFeatured
                                                    ? "text-4xl sm:text-6xl"
                                                    : "text-3xl sm:text-4xl"
                                            }`}
                                        >
                                            {c.title}
                                        </h3>
                                        <p className="mt-4 text-[hsl(var(--text-secondary))] leading-relaxed max-w-md text-sm sm:text-base">
                                            {c.description}
                                        </p>
                                    </div>
                                </article>
                            </FadeUp>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
